import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Box, Button, Skeleton, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Keyboard } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { DataDialog } from "@app/components/atoms";
import {
  formatSignatureAtDisplay,
  formatSignatureByDisplay,
  hasNonEmptySignatureUrl,
} from "./reportSignatureDisplay.helper";

const SIGNATURE_PRIMARY = "#140a9a";
const SIGNATURE_IMAGE_MIN_HEIGHT = 200;
const SIGNATURE_IMAGE_MAX_HEIGHT = 260;

const signatureNavButtonSx = {
  top: "50%",
  marginTop: 0,
  transform: "translateY(-50%)",
  zIndex: 2,
  transition:
    "background-color 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease",
  border: "1px solid transparent",
  "&:hover": {
    backgroundColor: "rgba(20, 10, 154, 0.1)",
    boxShadow: "0 2px 8px rgba(20, 10, 154, 0.18)",
    borderColor: "rgba(20, 10, 154, 0.2)",
  },
  "&:hover::after": {
    color: SIGNATURE_PRIMARY,
  },
};

export function SignatureCardSkeleton() {
  return (
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
      <Skeleton variant="text" width={72} height={24} />
      <Skeleton variant="circular" width={16} height={16} />
    </Box>
  );
}

type SignatureMeta = {
  signatureUrl?: string | null;
  signatureBy?: string | null;
  signatureAt?: string | null;
  lastUpdatedSignatureUrl?: string | null;
  lastUpdatedSignatureBy?: string | null;
  lastUpdatedSignatureAt?: string | null;
};

type ReportSignatureSectionProps = SignatureMeta & {
  userTimeFormat: string;
  showSignatureInFooter?: boolean;
};

type ReportDialogSignatureFooterProps = ReportSignatureSectionProps & {
  loading?: boolean;
  showSignatureInFooter?: boolean;
  children: ReactNode;
};

type ReportSignatureFieldProps = SignatureMeta & {
  userTimeFormat?: string;
  className?: string;
  labelClassName?: string;
};

function SignatureImagePreview({
  signatureUrl,
  imageLabel,
}: {
  signatureUrl: string;
  imageLabel: string;
}) {
  const [isImageLoading, setIsImageLoading] = useState(true);

  useEffect(() => {
    setIsImageLoading(true);
  }, [signatureUrl]);

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        width: "100%",
        minHeight: SIGNATURE_IMAGE_MIN_HEIGHT,
        backgroundColor: "#fafafa",
        borderRadius: 1,
        border: "1px solid #eee",
        p: 1,
      }}
    >
      {isImageLoading && (
        <Skeleton
          variant="rectangular"
          width="100%"
          height={SIGNATURE_IMAGE_MIN_HEIGHT}
          sx={{ borderRadius: 1 }}
        />
      )}
      <img
        src={signatureUrl}
        alt={imageLabel}
        style={{
          maxWidth: "100%",
          maxHeight: SIGNATURE_IMAGE_MAX_HEIGHT,
          objectFit: "contain",
          display: isImageLoading ? "none" : "block",
        }}
        onLoad={() => setIsImageLoading(false)}
        onError={(e) => {
          (e.currentTarget as HTMLImageElement).style.display = "none";
          setIsImageLoading(false);
        }}
      />
    </Box>
  );
}

function SignatureImageBlock({
  signatureUrl,
  imageLabel,
  signedByLabel,
  signedOnLabel,
  signatureBy,
  signatureAt,
  userTimeFormat,
}: {
  signatureUrl: string;
  imageLabel: string;
  signedByLabel: string;
  signedOnLabel: string;
  signatureBy?: string | null;
  signatureAt?: string | null;
  userTimeFormat: string;
}) {
  return (
    <Box sx={{ py: 1 }}>
      <Typography variant="body2" sx={{ fontWeight: 700, mb: 1 }}>
        {imageLabel}
      </Typography>
      <Box sx={{ mb: 2 }}>
        <SignatureImagePreview
          signatureUrl={signatureUrl}
          imageLabel={imageLabel}
        />
      </Box>

      <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
        {signedByLabel}
      </Typography>
      <Typography variant="body1" sx={{ mb: 1.5 }}>
        {formatSignatureByDisplay(signatureBy)}
      </Typography>

      <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
        {signedOnLabel}
      </Typography>
      <Typography variant="body1">
        {formatSignatureAtDisplay(signatureAt, userTimeFormat)}
      </Typography>
    </Box>
  );
}

type SignatureSlide = {
  signatureUrl: string;
  imageLabel: string;
  signedByLabel: string;
  signedOnLabel: string;
  signatureBy?: string | null;
  signatureAt?: string | null;
};

function SignatureViewSlider({
  slides,
  userTimeFormat,
}: {
  slides: SignatureSlide[];
  userTimeFormat: string;
}) {
  const paginationRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    setActiveIndex(0);
  }, [slides]);

  if (slides.length === 1) {
    return (
      <SignatureImageBlock {...slides[0]} userTimeFormat={userTimeFormat} />
    );
  }

  const activeSlide = slides[activeIndex] ?? slides[0];

  return (
    <Box sx={{ py: 1 }}>
      <Typography variant="body2" sx={{ fontWeight: 700, mb: 1 }}>
        {activeSlide.imageLabel}
      </Typography>

      <Box
        sx={{
          position: "relative",
          mb: 2,
          "& .signature-view-swiper": {
            width: "100%",
          },
          "& .signature-view-swiper .swiper-slide": {
            width: "100%",
            height: "auto",
            boxSizing: "border-box",
          },
          "& .signature-view-swiper .swiper-button-next, & .signature-view-swiper .swiper-button-prev":
            signatureNavButtonSx,
          "& .signature-view-swiper .swiper-button-prev": {
            left: "8px",
          },
          "& .signature-view-swiper .swiper-button-next": {
            right: "8px",
          },
        }}
      >
        <Swiper
          cssMode
          slidesPerView={1}
          navigation
          pagination={{
            clickable: true,
            el: paginationRef.current,
          }}
          onBeforeInit={(swiper) => {
            if (paginationRef.current) {
              swiper.params.pagination = {
                ...(typeof swiper.params.pagination === "object"
                  ? swiper.params.pagination
                  : {}),
                clickable: true,
                el: paginationRef.current,
              };
            }
          }}
          onSlideChange={(swiper) => setActiveIndex(swiper.activeIndex)}
          keyboard
          modules={[Navigation, Pagination, Keyboard]}
          className="swiper signature-view-swiper"
        >
          {slides.map((slide, index) => (
            <SwiperSlide key={`${slide.signatureUrl}-${index}`}>
              <SignatureImagePreview
                signatureUrl={slide.signatureUrl}
                imageLabel={slide.imageLabel}
              />
            </SwiperSlide>
          ))}
        </Swiper>
      </Box>

      <Box
        ref={paginationRef}
        className="signature-view-pagination"
        sx={{
          position: "static",
          marginTop: "12px",
          marginBottom: "16px",
          textAlign: "center",
          "& .swiper-pagination-bullet": {
            backgroundColor: "#bcbcbc",
            opacity: 1,
          },
          "& .swiper-pagination-bullet-active": {
            backgroundColor: SIGNATURE_PRIMARY,
          },
        }}
      />

      <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
        {activeSlide.signedByLabel}
      </Typography>
      <Typography variant="body1" sx={{ mb: 1.5 }}>
        {formatSignatureByDisplay(activeSlide.signatureBy)}
      </Typography>

      <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
        {activeSlide.signedOnLabel}
      </Typography>
      <Typography variant="body1">
        {formatSignatureAtDisplay(activeSlide.signatureAt, userTimeFormat)}
      </Typography>
    </Box>
  );
}

/** Dialog footer wrapper. Use showSignatureInFooter for inspection reports only. */
export function ReportDialogSignatureFooter({
  signatureUrl,
  signatureBy,
  signatureAt,
  lastUpdatedSignatureUrl,
  lastUpdatedSignatureBy,
  lastUpdatedSignatureAt,
  userTimeFormat,
  loading = false,
  showSignatureInFooter = false,
  children,
}: ReportDialogSignatureFooterProps) {
  if (!showSignatureInFooter) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          width: "100%",
          flexWrap: "wrap",
          gap: "12px",
        }}
      >
        {children}
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", display: "flex", flexDirection: "column" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
          flexWrap: "wrap",
          gap: "12px",
        }}
      >
        <Box
          sx={{
            minHeight: 24,
            display: "flex",
            alignItems: "center",
            marginLeft: "4px",
          }}
        >
          {loading ? (
            <SignatureCardSkeleton />
          ) : (
            <ReportSignatureSection
              signatureUrl={signatureUrl}
              signatureBy={signatureBy}
              signatureAt={signatureAt}
              lastUpdatedSignatureUrl={lastUpdatedSignatureUrl}
              lastUpdatedSignatureBy={lastUpdatedSignatureBy}
              lastUpdatedSignatureAt={lastUpdatedSignatureAt}
              userTimeFormat={userTimeFormat}
              showSignatureInFooter={showSignatureInFooter}
            />
          )}
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            flexWrap: "wrap",
            gap: "12px",
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
}

export function ReportSignatureLink({
  signatureUrl,
  signatureBy,
  signatureAt,
  lastUpdatedSignatureUrl,
  lastUpdatedSignatureBy,
  lastUpdatedSignatureAt,
  userTimeFormat = "yyyy/MM/dd HH:mm",
  showSignatureInFooter = false,
  dialogTitle,
}: SignatureMeta & {
  signatureUrl?: string | null;
  userTimeFormat?: string;
  showSignatureInFooter?: boolean;
  dialogTitle?: string;
}) {
  const { t } = useTranslation();
  const [viewOpen, setViewOpen] = useState(false);

  const hasOriginal = hasNonEmptySignatureUrl(signatureUrl);
  const hasLastUpdated = hasNonEmptySignatureUrl(lastUpdatedSignatureUrl);

  const signatureSlides = useMemo(() => {
    const slides: SignatureSlide[] = [];

    if (hasOriginal) {
      slides.push({
        signatureUrl: signatureUrl as string,
        imageLabel: t("reportSignature.signatureImage"),
        signedByLabel: t("reportSignature.signedBy"),
        signedOnLabel: t("reportSignature.signedAt"),
        signatureBy,
        signatureAt,
      });
    }

    if (hasLastUpdated) {
      slides.push({
        signatureUrl: lastUpdatedSignatureUrl as string,
        imageLabel: t("reportSignature.lastUpdatedSignatureImage"),
        signedByLabel: t("reportSignature.lastUpdatedSignedBy"),
        signedOnLabel: t("reportSignature.lastUpdatedSignedOn"),
        signatureBy: lastUpdatedSignatureBy,
        signatureAt: lastUpdatedSignatureAt,
      });
    }

    return slides;
  }, [
    hasOriginal,
    hasLastUpdated,
    signatureUrl,
    signatureBy,
    signatureAt,
    lastUpdatedSignatureUrl,
    lastUpdatedSignatureBy,
    lastUpdatedSignatureAt,
    t,
  ]);

  if (!signatureSlides.length) {
    return null;
  }

  return (
    <>
      {showSignatureInFooter && (
        <span style={{ marginInlineEnd: 4 }}>
          {`${t("createInspection.signature")}: `}
        </span>
      )}
      <span
        role="button"
        tabIndex={0}
        style={{
          cursor: "pointer",
          fontSize: "14px",
          display: "inline-flex",
          alignItems: "center",
          gap: "4px",
        }}
        onClick={() => setViewOpen(true)}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            setViewOpen(true);
          }
        }}
      >
        <span
          style={{
            textDecoration: "underline",
            color: SIGNATURE_PRIMARY,
            fontWeight: "bold",
          }}
        >
          {t("formAction.view")}
        </span>
      </span>

      <DataDialog
        open={viewOpen}
        onClose={() => setViewOpen(false)}
        title={dialogTitle ?? t("createInspection.signature")}
        fullWidth
        disableEnforceFocus
        className="dialog-container"
        dialogContentSx={{
          pt: 2,
          pb: 1,
        }}
        dialogActions={
          <Button
            className="btn btn-no-border"
            style={{ width: "auto" }}
            onClick={() => setViewOpen(false)}
          >
            {t("dialogRefuel.close")}
          </Button>
        }
      >
        <SignatureViewSlider
          slides={signatureSlides}
          userTimeFormat={userTimeFormat}
        />
      </DataDialog>
    </>
  );
}

type ReportLastUpdatedHeaderProps = {
  isUpdated?: boolean;
  lastUpdatedSignatureUrl?: string | null;
  lastUpdatedSignatureBy?: string | null;
  lastUpdatedSignatureAt?: string | null;
  userTimeFormat: string;
  /** Left margin of the block, to line up with the reporter card's own text indent. Defaults to "8px". */
  marginLeft?: string;
};

/** "[Edited] / Last updated by / Last updated on / Signature" block shown at the top of a report dialog, above the reporter card. */
export function ReportLastUpdatedHeader({
  isUpdated,
  lastUpdatedSignatureUrl,
  lastUpdatedSignatureBy,
  lastUpdatedSignatureAt,
  userTimeFormat,
  marginLeft = "8px",
}: ReportLastUpdatedHeaderProps) {
  const { t } = useTranslation();

  if (!isUpdated) {
    return null;
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "4px",
        marginLeft,
        marginBottom: 12,
      }}
    >
      <p
        className="txt-gray-1 fs-12 mt-0 mb-0"
        style={{ fontWeight: 500, margin: 0 }}
      >
        {t("dialogReport.edited")}
      </p>
      {lastUpdatedSignatureBy && (
        <p className="txt-black-1 fs-14 mt-0 mb-0" style={{ margin: 0 }}>
          {t("reportSignature.lastUpdatedSignedBy")}
          {": "}
          {formatSignatureByDisplay(lastUpdatedSignatureBy)}
        </p>
      )}
      <p className="txt-black-1 fs-14 mt-0 mb-0" style={{ margin: 0 }}>
        {t("reportSignature.lastUpdatedSignedOn")}
        {": "}
        {formatSignatureAtDisplay(lastUpdatedSignatureAt, userTimeFormat)}
      </p>
      {hasNonEmptySignatureUrl(lastUpdatedSignatureUrl) && (
        <p
          className="txt-black-1 fs-14 mt-0 mb-0"
          style={{ margin: 0, marginBottom: 8 }}
        >
          {t("createInspection.signature")}
          {": "}
          <ReportSignatureLink
            lastUpdatedSignatureUrl={lastUpdatedSignatureUrl}
            lastUpdatedSignatureBy={lastUpdatedSignatureBy}
            lastUpdatedSignatureAt={lastUpdatedSignatureAt}
            userTimeFormat={userTimeFormat}
            dialogTitle={t("reportSignature.lastUpdate")}
          />
        </p>
      )}
    </div>
  );
}

/** Inline signature field shown alongside location, SMR, and ODO in report content. */
export function ReportSignatureField({
  signatureUrl,
  signatureBy,
  signatureAt,
  lastUpdatedSignatureUrl,
  lastUpdatedSignatureBy,
  lastUpdatedSignatureAt,
  userTimeFormat,
  className = "text-content",
  labelClassName = "txt-gray-1 fs-12 mt-0 mb-0 txt-pre-wrap",
}: ReportSignatureFieldProps) {
  const { t } = useTranslation();

  if (
    !hasNonEmptySignatureUrl(signatureUrl) &&
    !hasNonEmptySignatureUrl(lastUpdatedSignatureUrl)
  ) {
    return null;
  }

  return (
    <>
      {hasNonEmptySignatureUrl(signatureUrl) && (
        <div className={className}>
          <p className={labelClassName}>{t("createInspection.signature")}</p>
          <p className="txt-black-1 fs-16 mt-0 mb-0 txt-pre-wrap">
            <ReportSignatureLink
              signatureUrl={signatureUrl}
              signatureBy={signatureBy}
              signatureAt={signatureAt}
              userTimeFormat={userTimeFormat}
            />
          </p>
        </div>
      )}
      {hasNonEmptySignatureUrl(lastUpdatedSignatureUrl) && (
        <div className={className}>
          <p className={labelClassName}>{t("reportSignature.lastUpdate")}</p>
          <p className="txt-black-1 fs-16 mt-0 mb-0 txt-pre-wrap">
            <ReportSignatureLink
              lastUpdatedSignatureUrl={lastUpdatedSignatureUrl}
              lastUpdatedSignatureBy={lastUpdatedSignatureBy}
              lastUpdatedSignatureAt={lastUpdatedSignatureAt}
              userTimeFormat={userTimeFormat}
              dialogTitle={t("reportSignature.lastUpdate")}
            />
          </p>
        </div>
      )}
    </>
  );
}

/** @deprecated Use ReportSignatureField in report content instead. */
export function ReportSignatureSection({
  signatureUrl,
  signatureBy,
  signatureAt,
  lastUpdatedSignatureUrl,
  lastUpdatedSignatureBy,
  lastUpdatedSignatureAt,
  userTimeFormat,
  showSignatureInFooter = false,
}: ReportSignatureSectionProps) {
  if (
    !hasNonEmptySignatureUrl(signatureUrl) &&
    !hasNonEmptySignatureUrl(lastUpdatedSignatureUrl)
  ) {
    return null;
  }

  return (
    <ReportSignatureLink
      signatureUrl={signatureUrl}
      signatureBy={signatureBy}
      signatureAt={signatureAt}
      lastUpdatedSignatureUrl={lastUpdatedSignatureUrl}
      lastUpdatedSignatureBy={lastUpdatedSignatureBy}
      lastUpdatedSignatureAt={lastUpdatedSignatureAt}
      userTimeFormat={userTimeFormat}
      showSignatureInFooter={showSignatureInFooter}
    />
  );
}
